/**
 * SkillDef — definition and instance of a player skill.
 *
 * Skills have levels (1+). Each level can add modifiers to the
 * ModifierBag and/or register hooks. Skills can also expose
 * out-of-combat values (e.g. Sneak's alert reduction) via getValue().
 *
 * The `apply` callback receives the bag AND the current level,
 * so each skill can scale its effects per level.
 */

import type { ModifierBag } from "../../../framework/stats";

/** How a skill registers its effects into the battle modifier bag. */
export type SkillApplyFn = (bag: ModifierBag, level: number) => void;

/**
 * Static skill definition — lives in the catalog.
 * One per skill type (e.g. "word_power", "sneak").
 */
export interface SkillDef {
  /** Unique identifier. */
  id: string;
  /** Display name. */
  name: string;
  /** Base description (level 0 / before acquiring). */
  description: string;
  /** Badge color for UI. */
  color: string;
  /** Icon character for UI badge. */
  icon: string;
  /** Maximum level (default 5). */
  maxLevel: number;
  /** Selection weight — higher = more likely to appear in choices. */
  weight: number;
  /**
   * Register modifiers and hooks into the battle bag.
   * Called by BattleBagBuilder at battle start.
   * Null means no in-combat effect (e.g. purely out-of-combat skill).
   */
  apply: SkillApplyFn | null;
  /**
   * Get a named out-of-combat value for this skill at a given level.
   * Used for things like Sneak's alert chance reduction.
   * Returns 0 if no such value exists.
   */
  getValue?: (level: number, key: string) => number;
  /**
   * Dynamic description showing current level info.
   * If not provided, uses `description`.
   */
  descFn?: (level: number) => string;
}

/**
 * SkillInstance — a skill the player has acquired, with a level.
 */
export class SkillInstance {
  readonly def: SkillDef;
  private _level: number;

  constructor(def: SkillDef, level: number = 1) {
    this.def = def;
    this._level = level;
  }

  get id(): string { return this.def.id; }
  get name(): string { return this.def.name; }
  get level(): number { return this._level; }
  get maxLevel(): number { return this.def.maxLevel; }
  get canLevelUp(): boolean { return this._level < this.def.maxLevel; }

  /** Increase level by 1 (capped at maxLevel). */
  levelUp(): void {
    if (this._level < this.def.maxLevel) {
      this._level++;
    }
  }

  /** Apply this skill's modifiers/hooks to a battle bag. */
  apply(bag: ModifierBag): void {
    this.def.apply?.(bag, this._level);
  }

  /** Get an out-of-combat value. */
  getValue(key: string): number {
    return this.def.getValue?.(this._level, key) ?? 0;
  }

  /** Get description for current level. */
  get description(): string {
    return this.def.descFn?.(this._level) ?? this.def.description;
  }
}
